import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link2 } from 'lucide-react';

interface RelatedLinksButtonProps {
  onClick: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const RelatedLinksButton: React.FC<RelatedLinksButtonProps> = ({
  onClick,
  className = '',
  size = 'md',
  label = 'لینک‌های مرتبط',
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    setIsAnimating(true);
    onClick();

    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => setIsAnimating(false), 400);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40,
        left: rect.left + rect.width / 2,
      });
    }
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sizeClasses = {
    sm: 'h-8 w-12',
    md: 'h-10 w-14',
    lg: 'h-12 w-16',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  return (
    <>
      {showTooltip &&
        createPortal(
          <div
            className="fixed transform -translate-x-1/2 z-[99999] pointer-events-none"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            <div className="bg-gray-800 text-white text-sm px-3 py-2 rounded-md whitespace-nowrap shadow-md animate-fadeIn">
              {label}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <button
        type="button"
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          relative inline-flex items-center justify-center rounded-full
          transition-all duration-300 transform
          ${sizeClasses[size]}
          bg-bg-secondary
          ${isHovering ? 'bg-bg-tertiary scale-110 shadow-lg' : ''}
          ${isAnimating ? 'scale-125 bg-primary/20' : ''}
          cursor-pointer hover:shadow-lg
          ${className}
        `}
        aria-label={label}
        title={label}
      >
        <Link2
          size={iconSizes[size]}
          className={`
            transition-all duration-300
            text-text-secondary
            ${isHovering ? 'text-primary scale-110' : ''}
            ${isAnimating ? 'animate-pulse text-primary' : ''}
          `}
          style={{
            transform: isHovering ? 'rotate(-15deg)' : 'rotate(0deg)',
            transition: 'all 0.3s ease-in-out',
          }}
          aria-hidden="true"
        />

        {isAnimating && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-full h-full rounded-full bg-primary/30 animate-ping absolute"></div>
          </div>
        )}
      </button>
    </>
  );
};

export default RelatedLinksButton;
